import { Button, Switch } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import { Form, Formik, useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';

// Renderless: streams current Formik values to the live preview on every change.
// Read-only — never touches the save path. We depend ONLY on `values`: the
// onValuesChange prop is typically a fresh inline arrow each render, so keeping
// it in a ref avoids an effect→setState→re-render→effect infinite loop.
export function FormikValuesWatcher({ onValuesChange }) {
  const { values } = useFormikContext();
  const cbRef = useRef(onValuesChange);
  cbRef.current = onValuesChange;
  // Guard on serialised content, not object identity: fire only when the values
  // actually change, so a re-render (e.g. from the preview provider updating)
  // can never re-trigger a stream and loop.
  const lastRef = useRef(null);
  useEffect(() => {
    const serialised = JSON.stringify(values);
    if (serialised === lastRef.current) return;
    lastRef.current = serialised;
    if (cbRef.current) cbRef.current(values);
  }, [values]);
  return null;
}

const useStyles = makeStyles({
  switch: {
    '& .MuiSwitch-thumb': {
      backgroundColor: '#fff'
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#e0e3e0'
    },
    '& .Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#4dfa43'
    }
  }
});

export const SectionBadge = {
  PRO: 'Pro'
};

const badgeClasses = {
  pro: 'bg-slate-700 text-white'
};

const CustomSection = ({
  id,
  title,
  description,
  renderBody,
  buttonTitle,
  onSave,
  badge = null,
  schema,
  initialValues,
  isForm = true,
  children,
  feature = null,
  shouldCheckFeature = false,
  showActiveToggle = false,
  handleActiveToggle,
  isEnabled,
  onRemove,
  onValuesChange
}) => {
  const classes = useStyles();
  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();
  const isFeatureEnabled = useMemo(() => {
    if (shouldCheckFeature) {
      return isFeatureForCompanyEnabled(feature);
    }

    return true;
  }, [feature, isFeatureForCompanyEnabled, shouldCheckFeature]);

  const validateSchema = schema => values => {
    return schema
      .validate(values, {
        abortEarly: false,
        strict: false
      })
      .then(() => ({}))
      .catch(({ inner }) => {
        return inner.reduce(
          (errorInfo, { path, message }) => ({
            ...errorInfo,
            [path]: (errorInfo[path] || []).concat(message)
          }),
          {}
        );
      });
  };

  return (
    <div
      className="flex flex-col p-6 bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 w-full h-fit relative"
      id={id}>
      {badge && !isFeatureEnabled && (
        <div
          className={`absolute top-3.5 right-3.5 text-xs font-semibold rounded-md px-2 py-1 w-fit leading-normal ${
            badgeClasses[badge.toLowerCase()] || ''
          }`}>
          {badge}
        </div>
      )}
      {showActiveToggle && handleActiveToggle && (
        <div className="absolute top-3.5 right-3.5 flex flex-row justify-end items-center text-slate-500">
          <div>Active</div>
          <Switch
            className={classes.switch}
            onChange={handleActiveToggle}
            checked={isEnabled ?? initialValues?.enabled ?? false}
          />
        </div>
      )}
      <div className="flex flex-row justify-between mb-4">
        <div className="flex flex-col gap-3.5">
          <div className="text-xl font-bold text-slate-800">{title}</div>
          <div className="text-sm text-slate-500">{description}</div>
        </div>
      </div>

      {isForm ? (
        <Formik
          onSubmit={onSave}
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={initialValues}
          enableReinitialize
          validate={validateSchema(schema)}>
          {formikProps => (
            <Form className="w-full h-fit">
              {onValuesChange && <FormikValuesWatcher onValuesChange={onValuesChange} />}
              {renderBody(formikProps)}

              <Button
                className="w-full !mt-4 !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !py-2.5 !rounded-xl !shadow-sm !normal-case"
                color="primary"
                variant="contained"
                type="submit">
                {buttonTitle}
              </Button>

              {onRemove && (
                <Button
                  className="w-full !mt-3 !text-red-600 !border-red-300 !font-semibold !py-2.5 !rounded-xl !normal-case"
                  variant="outlined"
                  type="button"
                  onClick={onRemove}>
                  Remove
                </Button>
              )}
            </Form>
          )}
        </Formik>
      ) : (
        children
      )}
    </div>
  );
};

export default CustomSection;
